import React, { useContext } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withFormik } from 'formik';

import {
  PrimaryButtonMedium as PrimaryButton,
  TextButtonSmall as TextButton,
} from '@xingternal/malt-button';

import Store from '../../../store';

import Input from '@xingternal/malt-text-input';
import { H, P } from '@xingternal/malt-typography';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';

// import DigitInput from './../../Components/DigitInput/DigitInput';
import IllustrationPlaceholder from './../../../Components/Illustrations/IllustrationPlaceholder';
import StretchButtonGroup from './../../../Components/StretchButtonGroup/StretchButtonGroup';
import ResendCodeLink from './../../../Components/ResendCodeLink/ResendCodeLink';
import Spacer from './../../../Components/Spacer/Spacer';
import PageviewTracker from '../../../Components/PageviewTracker/PageviewTracker';
import withActionTracker from '../../../Components/PageviewTracker/withActionTracker';

import api from './../../../utils/api';
import messages from './messages';

const TestableForm = testable(QA_MALT, 'form');

const withForm = withFormik({
  mapPropsToValues: () => ({
    token: '',
  }),
  handleSubmit: (
    { token },
    {
      setErrors,
      setSubmitting,
      props: {
        history: { replace, push },
        track,
      },
    }
  ) => {
    api
      .post('/recovery/password/resets/code', { token })
      .then(({ success }) => {
        if (success) replace('/recovery/password/new');
      })
      .catch(err => {
        track('welcome_login_password_recovery_error_code');
        if (err.jsonResponse && err.jsonResponse.error === 'LOGIN_REQUIRED') {
          push({
            pathname: '/recovery',
            search: `?error=${err.jsonResponse.error}`,
          });
          return;
        }

        setErrors({ code: err.jsonResponse.error });
        setSubmitting(false);
      });
  },
});

const PasswordRecoveryConfirm = ({
  handleChange,
  handleSubmit,
  setErrors,
  errors,
  isSubmitting,
  values: { token },
}) => {
  const {
    state: { username, knownUser },
  } = useContext(Store);

  if (!username) {
    return <Redirect to={'/recovery'} />;
  }

  const onResendSuccess = () => setErrors(false);
  const onResendError = err => {
    const errorString = err && err.jsonResponse && err.jsonResponse.error;
    errorString && setErrors({ code: errorString });
  };

  return (
    <>
      <PageviewTracker
        pageName={'wbm/Welcome/login/recovery/password/confirm'}
      />

      <IllustrationPlaceholder />

      <TestableForm
        onSubmit={handleSubmit}
        method={'POST'}
        testId={'code-input-form'}
      >
        <H level={1}>
          <FormattedMessage {...messages.title} />
        </H>
        <P>
          <FormattedMessage
            {...messages.description}
            values={{ username: <b>{username}</b> }}
          />
        </P>

        <Input
          inputMode={'numeric'}
          pattern={'[0-9]*'}
          name={'token'}
          size={'m'}
          type={'tel'}
          maxLength={6}
          onChange={handleChange}
          message={errors && errors.code}
          required
          autoFocus
        />

        <ResendCodeLink
          resendEndpoint={'/recovery/password/reset_requests'}
          resendPayload={knownUser ? null : { username }}
          onSuccess={onResendSuccess}
          onError={onResendError}
        />

        <Spacer size={2} />

        <StretchButtonGroup>
          <PrimaryButton
            disabled={token.length < 6}
            loading={isSubmitting}
            type={'submit'}
            testId={'code-input-next-button'}
          >
            <FormattedMessage {...messages.submitLabel} />
          </PrimaryButton>

          <Spacer size={2} />

          {!knownUser && (
            <TextButton to={'/recovery'} testId={'correct-email-link'}>
              <FormattedMessage {...messages.backLinkText} />
            </TextButton>
          )}
        </StretchButtonGroup>
      </TestableForm>
    </>
  );
};

PasswordRecoveryConfirm.propTypes = {
  handleSubmit: PropTypes.func,
  setErrors: PropTypes.func,
  errors: PropTypes.object,
  values: PropTypes.object,
  isSubmitting: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      username: PropTypes.string,
    }),
  }),
  track: PropTypes.func,
};

export default withActionTracker(withForm(PasswordRecoveryConfirm));
